import React, { useCallback, useMemo } from 'react'
import { Form, Input, Typography, App } from 'antd'
import { EyeTwoTone, EyeInvisibleOutlined } from '@ant-design/icons'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import AuthService from '@/libs/services/auth'
import AuthSubmitButton from '@/features/auth/components/AuthSubmitButton'
import {
  AuthForm,
  AuthLink,
  AuthFooterText,
  AuthTitleWrapper,
  AuthLabelAsterisk,
  AuthFormItemSubmit,
  AuthFormItemRow,
  AuthRow,
  AuthRowItem,
} from '@/features/auth/components/AuthFormStyles'

const { Title } = Typography

const RegisterView = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const { notification } = App.useApp()
  const licenseKey = searchParams.get('license')

  const onFinish = useCallback(
    async values => {
      try {
        const payload = {
          email: values.email,
          password: values.password,
          first_name: values.first_name,
          last_name: values.last_name,
        }
        if (licenseKey) payload.license = licenseKey
        const response = await AuthService.register(payload)

        if (response.status === 204) {
          notification.success({
            title: t('common.successTitle'),
            description: t('auth.register.success'),
            placement: 'topRight',
            duration: 3,
          })
          navigate('/auth/login', { replace: true })
        }
      } catch {
        notification.error({
          title: t('common.errorTitle'),
          description: t('auth.register.error'),
          placement: 'topRight',
          duration: 3,
        })
        form.setFields([
          {
            name: 'password',
            errors: [t('auth.register.error')],
          },
        ])
      }
    },
    [navigate, form, notification, licenseKey, t]
  )

  const formRules = useMemo(
    () => ({
      email: [
        { required: true, message: t('auth.login.passwordRequired') },
        { type: 'email', message: t('auth.login.invalidEmailAddress') },
      ],
      password: [{ required: true, message: t('auth.register.passwordRequired') }],
      first_name: [{ required: true, message: t('auth.register.firstNameRequired') }],
      last_name: [{ required: true, message: t('auth.register.lastNameRequired') }],
    }),
    [t]
  )

  const requiredLabel = text => (
    <>
      <AuthLabelAsterisk>*</AuthLabelAsterisk> {text}
    </>
  )

  return (
    <>
      <AuthTitleWrapper>
        <Title level={3}>{t('auth.register.title')}</Title>
      </AuthTitleWrapper>

      <AuthForm form={form} layout="vertical" requiredMark={false} onFinish={onFinish}>
        <Form.Item
          name="email"
          rules={formRules.email}
          validateTrigger="onBlur"
          label={requiredLabel(t('auth.login.email'))}
        >
          <Input placeholder={t('auth.register.email')} size="large" />
        </Form.Item>
        <AuthFormItemRow>
          <AuthRow>
            <AuthRowItem
              name="first_name"
              rules={formRules.first_name}
              validateTrigger="onBlur"
              label={requiredLabel(t('auth.register.firstName'))}
            >
              <Input placeholder={t('auth.register.firstName')} size="large" />
            </AuthRowItem>
            <AuthRowItem
              name="last_name"
              rules={formRules.last_name}
              validateTrigger="onBlur"
              label={requiredLabel(t('auth.register.lastName'))}
            >
              <Input placeholder={t('auth.register.lastName')} size="large" />
            </AuthRowItem>
          </AuthRow>
        </AuthFormItemRow>
        <Form.Item
          name="password"
          rules={formRules.password}
          validateTrigger="onBlur"
          label={requiredLabel(t('auth.register.password'))}
        >
          <Input.Password
            placeholder="••••••••••••"
            size="large"
            iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>

        <AuthFormItemSubmit>
          <AuthSubmitButton type="primary" htmlType="submit" block>
            {t('auth.register.submit')}
          </AuthSubmitButton>
        </AuthFormItemSubmit>
      </AuthForm>

      <AuthFooterText>
        {t('auth.register.subtitle')} <AuthLink to="/auth/login">{t('auth.login.title')}</AuthLink>
      </AuthFooterText>
    </>
  )
}

export default RegisterView
